import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * เครดิตโค้ด (กรอกรหัสรับเครดิต/เพชร) — ตรรกะฝั่งลูกค้า
 * ใช้ร่วมกันทั้ง landing และ landing-ver2 (ไฟล์นี้เหมือนกันทั้งสองแอป); หน้าตาแยกกันที่ pages/CreditCode.js
 *   • เซิร์ฟเวอร์ตัดสินทุกอย่าง (โค้ดมี/ซ้ำ/หมดอายุ/เทิร์น/เปิด-ปิดระบบ) — ฝั่งนี้แค่ส่งรหัสและแสดงผล
 *   • ทุกครั้งที่กรอก (สำเร็จ/ไม่สำเร็จ) เซิร์ฟเวอร์บันทึกประวัติ → โหลดมาแสดงในหน้า
 */

export const CODE_MAX = 64;

/** ล้างสิ่งที่พิมพ์/วาง: เหลือเฉพาะตัวอักษร ตัวเลข _ - (วางจาก LINE มักติดช่องว่าง/ขึ้นบรรทัด) แล้วทำเป็นตัวพิมพ์ใหญ่ */
export const cleanCode = (raw) =>
  String(raw ?? "")
    .replace(/[^\p{L}\p{N}_-]/gu, "")
    .toUpperCase()
    .slice(0, CODE_MAX);

/** แบ่งรหัสเป็นกลุ่ม ๆ ให้อ่านง่ายบนหน้าจอ (แสดงผลอย่างเดียว ไม่ได้เปลี่ยนค่าที่ส่ง) */
export const groupCode = (code, size = 4) => {
  const s = String(code || "");
  return s.length > size ? s.match(new RegExp(`.{1,${size}}`, "g")) : s ? [s] : [];
};

/** เหตุผลที่กรอกไม่สำเร็จ → หัวข้อสั้น + คำแนะนำ (ใช้ทั้งผลลัพธ์ทันทีและแถวในประวัติ) */
export const FAIL_INFO = {
  NOT_FOUND: { title: "ไม่พบโค้ดนี้", hint: "ตรวจตัวอักษรอีกครั้ง (ตัวเล็ก/ใหญ่ไม่ต่างกัน)" },
  ALREADY_USED: { title: "คุณใช้โค้ดนี้ไปแล้ว", hint: "โค้ดหนึ่งรหัสใช้ได้คนละครั้ง" },
  FULL: { title: "โค้ดนี้ถูกใช้ครบแล้ว", hint: "สิทธิ์ของโค้ดนี้หมดแล้ว รอโค้ดใหม่ได้เลย" },
  EXPIRED: { title: "โค้ดนี้หมดอายุแล้ว", hint: "สอบถามแอดมินเพื่อขอโค้ดใหม่" },
  DISABLED: { title: "โค้ดนี้ปิดใช้งานอยู่", hint: "โค้ดถูกปิดชั่วคราวโดยผู้ดูแล" },
  CLOSED: { title: "ระบบเครดิตโค้ดปิดให้บริการชั่วคราว", hint: "กลับมาลองใหม่ภายหลัง" },
  TURN_ACTIVE: { title: "ยังติดเทิร์นโปรโมชั่นเดิม", hint: "ทำเทิร์นโปรเดิมให้ครบก่อนจึงรับโค้ดได้" },
  RATE_LIMIT: { title: "กรอกผิดบ่อยเกินไป", hint: "รอสักครู่แล้วลองใหม่" },
  USER_STATUS: { title: "บัญชีนี้ไม่สามารถรับสิทธิ์ได้", hint: "ติดต่อแอดมินหากคิดว่าผิดพลาด" },
  BONUS_BLOCK: { title: "บัญชีนี้ถูกจำกัดการรับโบนัส", hint: "ติดต่อแอดมินหากคิดว่าผิดพลาด" },
  EMPTY: { title: "กรุณากรอกรหัสโค้ด", hint: "" },
  ERROR: { title: "ทำรายการไม่สำเร็จ", hint: "ลองใหม่อีกครั้ง" },
};
export const failInfo = (code) => FAIL_INFO[code] || FAIL_INFO.ERROR;

const nf = (v) => Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });

/** ผลที่เซิร์ฟเวอร์ตอบเมื่อสำเร็จ → สิ่งที่ต้องโชว์ในหน้าผลลัพธ์ */
export function rewardView(r) {
  if (!r) return null;
  const diamond = r.rewardType === "DIAMOND";
  const amount = Number(r.rewardAmount ?? r.amount ?? 0);
  const need = Number(r.turnover_required) || 0;
  const scoped = r.play_scope && r.play_scope !== "ALL";
  return {
    diamond,
    amount,
    big: diamond ? nf(amount) : `฿${nf(amount)}`,
    unit: diamond ? "เพชร" : "บาท",
    // longtext = รายละเอียดที่แอดมินตั้งไว้ หรือข้อความมาตรฐาน "รับเครดิต/เพชร N …" (ซึ่งซ้ำกับตัวเลขใหญ่ จึงไม่โชว์)
    detail: r.longtext && !/^รับ(เครดิต|เพชร) [\d,.]+ (บาท|เพชร)$/.test(r.longtext) ? r.longtext : "",
    turnover: need > 0 ? `ต้องแทงให้ครบ ฿${nf(need)} ก่อนถอนส่วนนี้` : "",
    scope: scoped && r.play_label ? `เครดิตนี้แทงได้${r.play_label}` : "",
    balance: r.balance_after != null ? Number(r.balance_after) : null,
  };
}

/** แถวประวัติจากเซิร์ฟเวอร์ → ข้อมูลพร้อมแสดง */
export function historyItem(h) {
  const ok = h.result === "SUCCESS";
  const diamond = h.reward_type === "DIAMOND";
  return {
    id: h.id,
    ok,
    code: h.code,
    at: h.created_at,
    title: ok ? (diamond ? `ได้รับ ${nf(h.amount)} เพชร` : `ได้รับ ฿${nf(h.amount)}`) : failInfo(h.reason).title,
    amountText: ok ? (diamond ? `+${nf(h.amount)} 💎` : `+฿${nf(h.amount)}`) : "",
  };
}

/** เวลาแบบสั้นอ่านง่าย — created_at มาได้ทั้ง ISO และ "YYYY-MM-DD HH:mm:ss" (เวลาไทย) */
export function whenText(v) {
  if (!v) return "";
  const d = new Date(typeof v === "string" ? v.replace(" ", "T") : v);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `วันนี้ ${time} น.`;
  if (d.toDateString() === yest.toDateString()) return `เมื่อวาน ${time} น.`;
  return `${d.toLocaleDateString("th-TH", { day: "numeric", month: "short" })} ${time} น.`;
}

/** ความคืบหน้าเทิร์นของโค้ดที่ยังติดอยู่ (0–100) */
export const turnPct = (a) => (a && a.turnover_required > 0 ? Math.max(0, Math.min(100, (a.turnover_done / a.turnover_required) * 100)) : 0);

const EMPTY_VIEW = { enabled: true, history: [], active: [] };

export function useCreditCode(onDone) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [code, setCodeState] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok:true, ...reward } | { ok:false, code, message }
  const [view, setView] = useState(null); // null = ยังโหลดไม่เสร็จ
  const alive = useRef(true);
  const inFlight = useRef(false);
  const cb = useRef(onDone);
  cb.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) return Promise.resolve();
    return Promise.resolve()
      .then(() => lottoApi.myCreditCode())
      .then((v) => alive.current && setView({ ...EMPTY_VIEW, ...(v || {}) }))
      .catch(() => alive.current && setView((prev) => prev || EMPTY_VIEW));
  }, [uid]);

  useEffect(() => {
    setView(null);
    setResult(null);
    load();
  }, [load]);

  const setCode = useCallback((v) => setCodeState(cleanCode(v)), []);

  const submit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (inFlight.current) return;
      if (!code) {
        setResult({ ok: false, code: "EMPTY", message: FAIL_INFO.EMPTY.title });
        return;
      }
      inFlight.current = true;
      setBusy(true);
      try {
        const res = await lottoApi.redeemCreditCode(code);
        if (alive.current) {
          setResult({ ok: true, ...res });
          setCodeState("");
        }
        if (cb.current) cb.current(res);
      } catch (err) {
        const d = err?.response?.data;
        if (alive.current) {
          // รหัสไม่ถูกเก็บไว้ในช่อง เพื่อให้แก้ตัวที่พิมพ์ผิดได้เลย
          setResult({ ok: false, code: d?.code || "ERROR", message: d?.message || FAIL_INFO.ERROR.title });
        }
      } finally {
        inFlight.current = false;
        if (alive.current) setBusy(false);
        load();
      }
    },
    [code, load],
  );

  return {
    isAuthenticated,
    code,
    setCode,
    busy,
    submit,
    result,
    closeResult: () => setResult(null),
    loading: !!uid && view === null,
    enabled: view ? view.enabled !== false : true,
    history: (view?.history || []).map(historyItem),
    active: view?.active || [],
    reload: load,
  };
}
